import React, {useMemo} from 'react';
import {Modal, Alert, Descriptions, Tag, Divider, Button, Space, Spin} from "antd";
import {SwapOutlined, WarningOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {useApiSearchMyStudentLessons, useApiStudentTransferLesson} from "@/api/lesson";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";

const WEEKDAYS = {
    saturday: 'شنبه',
    sunday: 'یکشنبه',
    monday: 'دوشنبه',
    tuesday: 'سه‌شنبه',
    wednesday: 'چهارشنبه',
    thursday: 'پنجشنبه',
    friday: 'جمعه',
};

function formatTimes(times = []) {
    return times
        .map(t => `${WEEKDAYS[t.weekday] || t.weekday} ${t.start}-${t.end}`)
        .join(' | ');
}

function MoveClassModalComponent({moveClassModal, setMoveClassModal, lessonDetail, id}) {
    const topic_id = lessonDetail?.topic_id;

    const {data: lessonList, isPending} = useApiSearchMyStudentLessons({
        enabled: moveClassModal,
        topic_id: topic_id,
        filter: 'active'
    });

    const {mutate: transferLesson, isPending: isPendingTransfer} = useApiStudentTransferLesson();

    // درس فعلی کاربر (ثبت‌نام فعلی)
    const currentEnrollment = lessonList?.data?.data?.at(0);
    const currentLesson = currentEnrollment?.lesson;
    const targetLesson = lessonDetail; // کلاس مقصد

    // محاسبه تفاوت‌ها
    const differences = useMemo(() => {
        if (!currentLesson || !targetLesson) return [];
        const diffs = [];

        if (currentLesson.title !== targetLesson.title) {
            diffs.push({
                label: 'عنوان کلاس',
                from: currentLesson.title,
                to: targetLesson.title,
            });
        }
        const currentAddr = currentLesson.times?.[0]?.classroom?.address;
        const targetAddr = targetLesson.times?.[0]?.classroom?.address;
        if (currentAddr && targetAddr && currentAddr !== targetAddr) {
            diffs.push({
                label: 'آدرس',
                from: currentAddr,
                to: targetAddr,
            });
        }
        const currentTimes = formatTimes(currentLesson.times);
        const targetTimes = formatTimes(targetLesson.times);
        if (currentTimes !== targetTimes) {
            diffs.push({
                label: 'زمان کلاس',
                from: currentTimes,
                to: targetTimes,
            });
        }
        if (currentLesson.code !== targetLesson.code) {
            diffs.push({
                label: 'کد درس',
                from: currentLesson.code,
                to: targetLesson.code,
            });
        }
        return diffs;
    }, [currentLesson, targetLesson]);

    function handleTransferLesson() {
        transferLesson({
            id: currentEnrollment?.lesson?.id, // id ثبت‌نام فعلی
            newId: id,                 // id درس مقصد
        }, {
            onSuccess: r => {
                ApiHandleSuccessMessage(r);
                setMoveClassModal(false);
            },
            onError: (e) => ApiHandleErrorMessage(e),
        });
    }

    return (
        <Modal
            open={moveClassModal}
            onCancel={() => setMoveClassModal(false)}
            title="جابجایی کلاس"
            width={720}
            footer={[
                <Button key="cancel" onClick={() => setMoveClassModal(false)}>
                    انصراف
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    icon={<SwapOutlined />}
                    loading={isPendingTransfer}
                    disabled={isPending || !currentEnrollment}
                    onClick={handleTransferLesson}
                >
                    جابجایی به این کلاس
                </Button>,
            ]}
        >
            {isPending ? (
                <div style={{textAlign: 'center', padding: 40}}>
                    <Spin />
                </div>
            ) : (
                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                    {currentEnrollment ? (
                        <Alert
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined />}
                            message="شما در این ترم در همین سرفصل کلاس دارید"
                            description={
                                <span>
                                    در حال حاضر در کلاس <b>{currentLesson?.title}</b> ثبت‌نام هستید.
                                    در صورت تمایل می‌توانید کلاس خود را به کلاس زیر جابجا کنید.
                                </span>
                            }
                        />
                    ) : (
                        <Alert
                            type="warning"
                            showIcon
                            message="کلاس فعالی در این سرفصل یافت نشد"
                        />
                    )}

                    <Alert
                        type="warning"
                        showIcon
                        icon={<WarningOutlined />}
                        message="شرایط جابجایی کلاس"
                        description={
                            <ul style={{marginBottom: 0, paddingRight: 18}}>
                                <li>جابجایی فقط در بازه زمانی انتخاب درس و حذف و اضافه امکان‌پذیر است.</li>
                                <li>در کلاس فعلی نباید برای شما نمره یا غیبت ثبت شده باشد.</li>
                                <li>ظرفیت کلاس جدید باید دارای جای خالی باشد.</li>
                            </ul>
                        }
                    />

                    <Divider orientation="right">مقایسه کلاس فعلی و کلاس جدید</Divider>

                    {differences.length === 0 ? (
                        <Alert type="success" showIcon message="این دو کلاس از نظر اطلاعات تفاوتی ندارند." />
                    ) : (
                        <Descriptions
                            bordered
                            column={1}
                            size="small"
                            labelStyle={{width: 120, fontWeight: 'bold'}}
                        >
                            {differences.map((d, idx) => (
                                <Descriptions.Item key={idx} label={d.label}>
                                    <div>
                                        <Tag color="red">فعلی</Tag> {d.from}
                                    </div>
                                    <div style={{marginTop: 4}}>
                                        <Tag color="green">جدید</Tag> {d.to}
                                    </div>
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    )}
                </Space>
            )}
        </Modal>
    );
}

export default MoveClassModalComponent;